/**
 * YouTube Studio numbers for the signed-in creator's own channel —
 * GET /api/user/youtube-analytics (the YouTube Analytics API, read with the
 * creator's own Google connection).
 *
 * Every figure is one YouTube returned, under YouTube's own metric name; the
 * page may change a unit (minutes → hours) but never computes a new metric
 * (YouTube API policy — see bumble-bee app/services/youtube_analytics.py).
 * The route always answers 200 with a `status` the page acts on.
 */
import apiConfig from '../config/apiConfig';

export type AnalyticsPeriod = 28 | 90 | 365;
export const ANALYTICS_PERIODS: AnalyticsPeriod[] = [28, 90, 365];

export interface AnalyticsTotals {
  views?: number;
  estimatedMinutesWatched?: number;
  averageViewDuration?: number;     // seconds
  averageViewPercentage?: number;   // 0–100
  subscribersGained?: number;
  subscribersLost?: number;
  likes?: number;
  comments?: number;
  shares?: number;
}
export interface DailyRow {
  day: string;                      // YYYY-MM-DD
  views?: number;
  estimatedMinutesWatched?: number;
  subscribersGained?: number;
  subscribersLost?: number;
}
export interface AgeRow { ageGroup: string; viewerPercentage: number }
export interface GenderRow { gender: string; viewerPercentage: number }
export interface CountryRow { country: string; views: number; estimatedMinutesWatched?: number }
export interface TrafficSourceRow { insightTrafficSourceType: string; views: number; estimatedMinutesWatched?: number }
export interface DeviceRow { deviceType: string; views: number; estimatedMinutesWatched?: number }
export interface ContentTypeRow { creatorContentType: string; views: number; estimatedMinutesWatched?: number }
export interface SubscribedStatusRow { subscribedStatus: string; views: number; estimatedMinutesWatched?: number }
export interface SearchTermRow { insightTrafficSourceDetail: string; views: number }
export interface TopVideoRow {
  video: string;
  views: number;
  estimatedMinutesWatched?: number;
  averageViewDuration?: number;
  averageViewPercentage?: number;
  subscribersGained?: number;
  title?: string | null;
  thumbnail_url?: string | null;
  published_at?: string | null;
}

export interface AnalyticsSections {
  totals?: AnalyticsTotals[];
  daily?: DailyRow[];
  age_groups?: AgeRow[];
  genders?: GenderRow[];
  countries?: CountryRow[];
  traffic_sources?: TrafficSourceRow[];
  devices?: DeviceRow[];
  content_types?: ContentTypeRow[];
  subscribed_status?: SubscribedStatusRow[];
  top_videos?: TopVideoRow[];
  search_terms?: SearchTermRow[];
  /** YouTube's ranking of the period's videos by subscribers gained */
  top_by_subscribers?: TopVideoRow[];
  /** the same totals for the equal period just before, as YouTube counts them */
  previous_totals?: AnalyticsTotals[];
}
export type AnalyticsSectionName = keyof AnalyticsSections;

export interface YouTubeAnalytics {
  status: 'ok';
  period: { days: number; start_date: string; end_date: string };
  previous_period?: { start_date: string; end_date: string };
  channel: { handle?: string | null; channel_id?: string | null };
  sections: AnalyticsSections;
  /** Reports YouTube couldn't produce for this channel, with YouTube's reason. */
  unavailable: Partial<Record<AnalyticsSectionName, string>>;
  fetched_at: string;
}

/** Why there is nothing to show, and so what the page offers:
 *  not_connected → connect; needs_reconnect → reconnect with the analytics permission;
 *  api_not_enabled → ours to fix; rate_limited / error → try again. */
export interface YouTubeAnalyticsBlocked {
  status: 'not_connected' | 'needs_reconnect' | 'api_not_enabled' | 'rate_limited' | 'error';
  message?: string;
}

export type YouTubeAnalyticsResponse = YouTubeAnalytics | YouTubeAnalyticsBlocked;

export async function getYouTubeAnalytics(
  authToken: string,
  days: AnalyticsPeriod,
  refresh = false,
): Promise<YouTubeAnalyticsResponse> {
  const params = new URLSearchParams({ days: String(days) });
  if (refresh) params.set('refresh', 'true');
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-analytics?${params}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) throw new Error(`YouTube analytics request failed (${response.status})`);
  return response.json();
}

/** One of the latest public uploads, with YouTube's numbers for its own first week. */
export interface VideoStart {
  video: string;
  published_at: string;
  title?: string | null;
  thumbnail_url?: string | null;
  /** the days read: the first `window_days`, or fewer for a newer upload (cut at today) */
  window: { start_date: string; end_date: string; days: number };
  views?: number;
  estimatedMinutesWatched?: number;
  averageViewPercentage?: number;
  /** YouTube couldn't report this video; its reason */
  unavailable?: string;
  /** its first week of thumbnail reach, when the creator turned the reports on */
  reach?: ReachWindow;
  /** YouTube's own format for it: SHORTS, VIDEO_ON_DEMAND, LIVE_STREAM… */
  creatorContentType?: string;
}

/** Thumbnail impressions and click-through rate over some days, from YouTube's daily reach
 *  reports (the rate is YouTube's ratio, 0.045 = 4.5%); `days` = how many days had a file. */
export interface ReachWindow {
  video_thumbnail_impressions: number;
  video_thumbnail_impressions_ctr: number | null;
  days: number;
}
/** Whether YouTube's reach reports are on for the channel: `off` until the creator turns them
 *  on, `preparing` until YouTube's first file (within ~2 days), then `ok`. */
export type ReachStatus =
  | { status: 'off' }
  | { status: 'preparing'; since?: string | null }
  | { status: 'ok'; since?: string | null; coverage: { start_date: string; end_date: string }; last_28: ReachWindow | null }
  | { status: 'unavailable'; reason?: string; message?: string };

export interface VideoStarts {
  status: 'ok';
  window_days: number;
  videos: VideoStart[];
  reach?: ReachStatus;
  fetched_at: string;
}
export type VideoStartsResponse = VideoStarts | YouTubeAnalyticsBlocked;

export async function getYouTubeVideoStarts(authToken: string, refresh = false): Promise<VideoStartsResponse> {
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-analytics/recent-videos${refresh ? '?refresh=true' : ''}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) throw new Error(`YouTube recent videos request failed (${response.status})`);
  return response.json();
}

/** One point of YouTube's audience-retention report (elapsedVideoTimeRatio 0.01…1). */
export interface RetentionPoint {
  elapsedVideoTimeRatio: number;
  /** viewers still watching at this point, as a ratio (can pass 1 where people rewatch) */
  audienceWatchRatio?: number;
  /** YouTube's comparison with every video of similar length: 0–1, 0.5 is typical */
  relativeRetentionPerformance?: number;
}
/** One of the creator's videos up close, over its life so far — all YouTube's numbers. */
export interface VideoDetail {
  status: 'ok';
  video: { video: string; title?: string | null; published_at?: string | null; thumbnail_url?: string | null; duration_seconds?: number | null };
  period: { start_date: string; end_date: string };
  points: RetentionPoint[];
  sections?: {
    totals?: AnalyticsTotals[];
    traffic_sources?: TrafficSourceRow[];
    search_terms?: SearchTermRow[];
  };
  unavailable?: Partial<Record<'totals' | 'traffic_sources' | 'search_terms', string>>;
  reach?: ReachStatus & { first_week?: ReachWindow | null };
  fetched_at: string;
}
export type VideoDetailResponse = VideoDetail | YouTubeAnalyticsBlocked;

export async function getYouTubeVideoDetail(authToken: string, video: string, refresh = false): Promise<VideoDetailResponse> {
  const params = new URLSearchParams({ video });
  if (refresh) params.set('refresh', 'true');
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-analytics/video?${params}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) throw new Error(`YouTube video request failed (${response.status})`);
  return response.json();
}

/** The creator's click: ask YouTube to prepare daily thumbnail-reach reports for their channel. */
export async function startYouTubeReach(authToken: string): Promise<{ status: string; message?: string }> {
  const response = await fetch(`${apiConfig.baseUrl}/api/user/youtube-analytics/reach/start`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!response.ok) throw new Error(`Couldn't turn on click-through reports (${response.status})`);
  return response.json();
}
